/**
 * usePageOgPublish — the publish / share / OG-capture sub-system of PageRenderer.
 *
 * Self-contained: owns its own state (publicUrl, copied, publishing, error,
 * publishStatus, draftHtmlHash) and the effects that drive it (publicUrl
 * re-seed on page swap, draft-html staleness hash, patch-failed banner
 * suppression). Returns the handlers + status state the orchestrator threads
 * into PageStatusBar.
 *
 * One-way dependency: imports leaves only (thumbnail-capture, endpoints,
 * page-cache, page-renderer-helpers, query keys, env). NEVER imports
 * PageRenderer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { queryKeys } from '@/lib/query/keys';
import { env } from '@/lib/env';
import {
  captureOgImage,
  prepareThumbnailHtml,
  sha256Hex,
  normalizeThumbnailError,
} from '@/lib/pages/thumbnail-capture';
import { uploadPageOgImage } from '@/lib/api/endpoints/objects';
import { resolveUrl } from '../utils/page-renderer-helpers';
import { patchObjectInCaches } from '../lib/page-cache';
import { OG_PREP_TIMEOUT_MS, OG_VIRTUAL_H, OG_VIRTUAL_W } from '../constants';
import type { PagePublishState } from '../types';

interface UsePageOgPublishArgs {
  object: any;
  token: string | null | undefined;
  html: string | undefined;
  effectiveHtml: string | undefined;
  title: string | undefined;
  publish: PagePublishState | undefined;
  matchesLiveId: (incoming: unknown) => boolean;
}

export function usePageOgPublish({
  object,
  token,
  html,
  effectiveHtml,
  title,
  publish,
  matchesLiveId,
}: UsePageOgPublishArgs) {
  const queryClient = useQueryClient();

  const [publishing, setPublishing] = useState(false);
  const [publicUrl, setPublicUrl] = useState(publish?.publicUrl || publish?.url || '');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Single-line status pill for the publish flow. We hold ONE active label
  // at a time (per `feedback_status_single_line.md`) — verb variety comes
  // from replacing the text in place rather than stacking prior states.
  // `null` means no pill is shown; the existing `Ready` / `error` text in
  // the status bar still drives the rest of the row.
  const [publishStatus, setPublishStatus] = useState<string | null>(null);

  // Hash of the page html that the currently-published OG image was built
  // from. Compared against `sha256Hex(effectiveHtml)` to decide whether to
  // show the "share preview is from an earlier version" pill. We only do
  // this when the published OG is a `client-capture` — generic server
  // fallbacks are not tied to a specific html version, so they're never
  // "stale" in a meaningful sense.
  const [draftHtmlHash, setDraftHtmlHash] = useState<string | null>(null);

  // Listen for patch-failure events from both the app layer (xavior:patch-failed
  // CustomEvent fired by useChat when findSectionRange returns null) and from
  // the iframe layer (xavior-patch-failed postMessage forwarded below as the
  // same CustomEvent). Surface the failure as an inline error so the user
  // knows the edit did not land rather than seeing a silent no-op.
  useEffect(() => {
    const onFail = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || !matchesLiveId(detail.moduleId)) return;
      // The user-facing banner here was more confusing than helpful — patch
      // failures are usually transient (race against a fresh build, stale
      // section id after a rebuild, etc.) and the existing srcDoc-reload
      // path silently retries. Keep the channel wired so we can re-add a
      // banner behind a flag if needed, but no longer surface a status pill.
      console.warn('[PageRenderer] patch-failed (banner suppressed):', detail);
    };
    window.addEventListener('xavior:patch-failed', onFail);
    return () => window.removeEventListener('xavior:patch-failed', onFail);
  }, [matchesLiveId]);

  // Re-seed publicUrl ONLY on page swap. Within a single object's
  // lifetime, handlePublish and handleUnpublish are the source of truth
  // for local state — they call setPublicUrl directly and patch the
  // cache optimistically.
  //
  // Why not also react to publish-prop changes for the same object: the
  // earlier "if (next) setPublicUrl(next)" guard fixed the post-publish
  // race (where a stale empty refetch wiped the freshly-set URL) but
  // INTRODUCED an inverse race on unpublish — if a stale post-publish
  // refetch lands AFTER unpublish completes, the prop is non-empty and
  // local state gets reverted, making the button look like the unpublish
  // didn't take. By only re-seeding on a real page swap, both directions
  // are stable: local state stays whatever the user's last action set
  // it to, and the cache eventually catches up via the explicit
  // `patchObjectInCaches` calls + the trailing invalidate.
  const prevObjectIdRef = useRef<string | undefined>(object?.id);
  useEffect(() => {
    if (prevObjectIdRef.current !== object?.id) {
      prevObjectIdRef.current = object?.id;
      setPublicUrl(publish?.publicUrl || publish?.url || '');
    }
  }, [publish?.publicUrl, publish?.url, object?.id]);

  // Hash the current draft html so the staleness pill can compare it
  // against the captured OG's `contentHash`. Recomputed whenever the html
  // changes — sha256 of an 8-100 KB string is sub-millisecond, so we
  // don't bother memoising. Reset to null between html transitions so we
  // never display a pill against a stale hash.
  useEffect(() => {
    if (!effectiveHtml) {
      setDraftHtmlHash(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const hash = await sha256Hex(effectiveHtml);
        if (!cancelled) setDraftHtmlHash(hash);
      } catch {
        if (!cancelled) setDraftHtmlHash(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveHtml]);

  /**
   * Capture + upload an OG image for the current draft html.
   *
   * Returns true on success, false on any failure (capture timeout, render
   * error, upload error). NEVER throws — callers treat the OG step as
   * non-blocking per design §6.1: if this fails the publish proceeds and
   * the backend renders a generic fallback OG image.
   *
   * Implementation notes:
   *   - Off-screen iframe sized to 1280×672 (desktop viewport at OG
   *     1.91:1 aspect — `OG_VIRTUAL_W × OG_VIRTUAL_H`), `srcdoc` set to
   *     the same `prepareThumbnailHtml(currentHtml)` we use for list
   *     thumbnails. The capture *output* is 1200×630 (the canonical OG
   *     canvas) — html-to-image downscales the 1280-wide source to the
   *     1200-wide canvas. Sandbox is `allow-same-origin` (no scripts)
   *     so html-to-image can walk the document.
   *   - Hard 10s cap on the entire prep phase (`OG_PREP_TIMEOUT_MS`); if
   *     the capture races past that, we abandon the iframe and let the
   *     publish proceed without an uploaded OG.
   *   - The iframe is removed in a finally block whether the race won or
   *     lost, so a slow capture doesn't leak DOM.
   */
  const ensurePageOgCapture = useCallback(async (
    currentHtml: string,
    objectVersion: number | undefined,
  ): Promise<boolean> => {
    if (!token) return false;
    if (typeof objectVersion !== 'number' || !Number.isFinite(objectVersion)) return false;

    const startedAt = performance.now();
    const contentHashPromise = sha256Hex(currentHtml);
    console.info('[og-debug] capture: start', {
      objectId: object?.id,
      objectVersion,
      htmlLength: currentHtml.length,
    });

    let captureIframe: HTMLIFrameElement | null = null;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
    }, OG_PREP_TIMEOUT_MS);

    try {
      captureIframe = document.createElement('iframe');
      captureIframe.setAttribute('aria-hidden', 'true');
      // `allow-same-origin` lets html-to-image walk the document; no
      // `allow-scripts` so nothing inside can execute.
      captureIframe.setAttribute('sandbox', 'allow-same-origin');
      captureIframe.style.cssText =
        `position:fixed;left:-99999px;top:-99999px;width:${OG_VIRTUAL_W}px;height:${OG_VIRTUAL_H}px;border:0;visibility:hidden;pointer-events:none;`;
      captureIframe.srcdoc = prepareThumbnailHtml(currentHtml);
      document.body.appendChild(captureIframe);

      // Race the iframe load + capture against the hard cap.
      const captureRace = (async () => {
        await new Promise<void>((resolve) => {
          captureIframe!.addEventListener('load', () => resolve(), { once: true });
        });
        if (timedOut) return null;
        return captureOgImage(captureIframe!);
      })();

      const timeoutRace = new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), OG_PREP_TIMEOUT_MS);
      });

      const blob = await Promise.race([captureRace, timeoutRace]);
      const captureMs = Math.round(performance.now() - startedAt);
      if (timedOut || !blob) {
        console.warn('[og-debug] capture: failed', {
          objectId: object?.id,
          reason: timedOut ? 'timeout' : 'no-blob',
          captureMs,
        });
        return false;
      }
      console.info('[og-debug] capture: ok', {
        objectId: object?.id,
        blobBytes: blob.size,
        captureMs,
      });

      const contentHash = await contentHashPromise;
      await uploadPageOgImage(token, object.id, {
        png: blob,
        contentHash,
        ifMatch: `"${objectVersion}"`,
      });
      console.info('[og-debug] upload: ok', {
        objectId: object?.id,
        contentHashPrefix: contentHash.slice(0, 12),
        totalMs: Math.round(performance.now() - startedAt),
      });
      return true;
    } catch (err) {
      const reason = normalizeThumbnailError(err);
      const status = (err as { status?: number } | null)?.status;
      console.warn('[og-debug] capture/upload error', {
        objectId: object?.id,
        reason,
        status,
        totalMs: Math.round(performance.now() - startedAt),
      });
      return false;
    } finally {
      clearTimeout(timeoutId);
      if (captureIframe && captureIframe.parentNode) {
        captureIframe.parentNode.removeChild(captureIframe);
      }
    }
  }, [token, object?.id]);

  const handlePublish = useCallback(async () => {
    if (publishing) return;
    setPublishing(true);
    setError(null);
    try {
      // Step 1 — OG prep. Single status pill; we replace the text rather
      // than stacking. Capture is best-effort — a failure swaps the pill
      // to a soft warning and still proceeds to publish.
      const currentHtml = effectiveHtml;
      const objectVersion: number | undefined =
        typeof object?.version === 'number' ? object.version : undefined;

      if (currentHtml && token && typeof objectVersion === 'number') {
        setPublishStatus('Preparing share image…');
        const ok = await ensurePageOgCapture(currentHtml, objectVersion);
        if (!ok) {
          setPublishStatus("Couldn't create share image — using generic preview");
        }
      }

      // Step 2 — publish. The backend reads `attrs.ogImage` (just-uploaded
      // capture) or falls back to a generic server-rendered OG, so this
      // call is the same shape regardless of capture outcome.
      setPublishStatus('Publishing…');
      const res = await fetch(`${env.apiBaseUrl}/pages/${object.id}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Workspace-Id': getActiveWorkspaceId(),
        },
      });
      if (!res.ok) throw new Error(`Publish failed: ${res.status}`);
      const data = await res.json().catch(() => ({}));
      // The publish endpoint returns `mapObject(updated)` which spreads
      // entity attributes onto the top level (apps/api/src/mappers/object-mapper.ts),
      // so the published url lives at `data.publish.publicUrl`. The
      // legacy `data.url`/`data.publicUrl` reads are kept as a defensive
      // fallback in case a future endpoint shape adds them, but the
      // primary path is the nested one — without this read, the
      // optimistic-patch branch below never fires.
      const nextPublicUrl: string =
        data?.publish?.publicUrl ||
        data?.publish?.url ||
        data?.url ||
        data?.publicUrl ||
        '';
      if (nextPublicUrl) {
        setPublicUrl(nextPublicUrl);
      }
      // Optimistic cache patch: overlay the new publish state onto every
      // cached object-list query that contains this page. Doing this
      // BEFORE invalidate prevents the in-flight refetch from racing the
      // publicUrl re-seed effect — by the time the refetch lands, the cache
      // already shows the published state, so PageRenderer never re-renders
      // with a stale (empty) publish prop. The follow-up invalidate keeps
      // eventual consistency with the server.
      if (nextPublicUrl) {
        // Prefer the server's full publish payload (slug, ogImage,
        // visibility, publishedAt) when present so the cache reflects
        // the real post-publish state. Fall back to a minimal shape
        // when the server didn't return one.
        const responsePublish = data?.publish && typeof data.publish === 'object'
          ? data.publish
          : null;
        patchObjectInCaches(queryClient, object.id, (obj: any) => {
          const nextPublish = responsePublish
            ? { ...responsePublish }
            : {
                ...(obj?.publish ?? obj?.attributes?.publish ?? {}),
                url: nextPublicUrl,
                publicUrl: nextPublicUrl,
              };
          return {
            ...obj,
            publish: nextPublish,
          };
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.all });
      setPublishStatus('Published');
      setTimeout(() => setPublishStatus(null), 2500);
    } catch (err) {
      console.error('[PageRenderer] Publish failed:', err);
      setError('Failed to publish');
      setPublishStatus(null);
      setTimeout(() => setError(null), 3000);
    } finally {
      setPublishing(false);
    }
  }, [publishing, token, object?.id, object?.version, queryClient, effectiveHtml, ensurePageOgCapture]);

  const resolvedUrl = publicUrl ? resolveUrl(publicUrl) : null;

  // Two distinct actions, two distinct buttons:
  //   - handleCopy:        always copies to clipboard. Works everywhere.
  //   - handleNativeShare: opens the OS share sheet (when supported).
  //
  // Previously these were combined: a single button toggled between
  // "Share" (navigator.share) and "Copy link" depending on browser
  // support. That meant on desktop Chrome/Safari (where navigator.share
  // IS available) the button was "Share" and clicking it opened the
  // native sheet — which on macOS doesn't always include a Copy Link
  // option. Users were left with no way to copy the link directly. The
  // split below makes Copy Link unconditionally available.
  const handleCopy = useCallback(async () => {
    if (!resolvedUrl) return;
    try {
      await navigator.clipboard.writeText(resolvedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('[PageRenderer] clipboard.writeText failed', err);
    }
  }, [resolvedUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!resolvedUrl) return;
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return;
    try {
      // Pass title + text in addition to url. Chrome on macOS has an
      // open issue where the Copy Link entry in the native share sheet
      // is rendered but inert when only `url` is supplied — supplying
      // the full triple makes the sheet treat the share as a "complete"
      // payload and re-enables Copy Link's click handler.
      await navigator.share({
        title: title || 'Shared from Xavior',
        text: title ? `${title}` : 'Shared from Xavior',
        url: resolvedUrl,
      });
    } catch {
      /* user cancelled */
    }
  }, [resolvedUrl, title]);

  // Compute share-sheet support once per render. `navigator.share` is
  // gated on a secure context AND user-gesture origin, but the function
  // existing is the cheap readiness signal we use to decide whether to
  // show the button at all.
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleDownload = useCallback(() => {
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'page'}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [html, title]);

  const handleUnpublish = useCallback(async () => {
    setError(null);
    try {
      // Server defines POST /:objectId/unpublish (not DELETE /publish)
      const res = await fetch(`${env.apiBaseUrl}/pages/${object.id}/unpublish`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Workspace-Id': getActiveWorkspaceId(),
        },
      });
      if (!res.ok) throw new Error(`Unpublish failed: ${res.status}`);
      setPublicUrl('');
      setPublishStatus(null);
      // Mirror the publish path: optimistically clear the publish state in
      // every cached object-list before invalidate, so the button stays in
      // the unpublished state without a momentary flicker back to
      // "Unpublish" while the refetch is in flight.
      patchObjectInCaches(queryClient, object.id, (obj: any) => ({
        ...obj,
        publish: undefined,
      }));
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.all });
    } catch (err) {
      console.error('[PageRenderer] Unpublish failed:', err);
      setError('Failed to unpublish');
      setTimeout(() => setError(null), 3000);
    }
  }, [token, object?.id, queryClient]);

  return {
    publishing,
    publicUrl,
    copied,
    error,
    publishStatus,
    draftHtmlHash,
    canNativeShare,
    handlePublish,
    handleUnpublish,
    handleCopy,
    handleNativeShare,
    handleDownload,
  };
}
